import type { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ChatApiService, UserApiService } from '@dev-fast/backend-services';
import type { IChatAccess, IMessage, IStickerPack } from '@dev-fast/types';
import type { NgxsOnInit, StateContext } from '@ngxs/store';
import { Action, Selector, State, Store } from '@ngxs/store';
import { patch, removeItem } from '@ngxs/store/operators';
import type { Observable } from 'rxjs';
import { catchError, of, tap } from 'rxjs';

import { NotificationsService } from '@app/core/notification-service';

import {
  CheckCanChat,
  CreateMsgEventHandling,
  DeleteMsgEventHandling,
  GetMessagesList,
  ScrollToBottom,
  SendMessage,
  SetActiveStickerPack,
  SetChatID,
} from './chat.actions';
import type { ChatStateModel } from './chat-state.model';
import { CHAT_INITIAL_STATE } from './chat-state.model';

const CHAT_MESSAGES_LIMIT = 50;

@State<ChatStateModel>({
  name: 'chat',
  defaults: CHAT_INITIAL_STATE,
})
@Injectable()
export class ChatState implements NgxsOnInit {
  readonly #store = inject(Store);
  readonly #api = inject(ChatApiService);
  readonly #userApi = inject(UserApiService);
  readonly #notyService = inject(NotificationsService);

  @Selector()
  static items({ items }: ChatStateModel): IMessage[] {
    return items;
  }

  @Selector()
  static chatId({ chatId }: ChatStateModel): string {
    return chatId;
  }

  @Selector()
  static stickers({ stickers }: ChatStateModel): IStickerPack[] {
    return stickers;
  }

  @Selector()
  static chatAccess({ chatAccess }: ChatStateModel): IChatAccess {
    return chatAccess;
  }

  @Selector()
  static activeStickerPack({ activeStickerPack }: ChatStateModel): IStickerPack {
    return activeStickerPack;
  }

  @Selector()
  static isAnimationPlayed({ isAnimationPlayed }: ChatStateModel): boolean {
    return isAnimationPlayed;
  }

  ngxsOnInit(): void {
    this.#api.messageCreatedEvent((msg: IMessage) => this.#store.dispatch(new CreateMsgEventHandling(msg)));
    this.#api.messageDeletedEvent((msg: IMessage) => this.#store.dispatch(new DeleteMsgEventHandling(msg)));
  }

  @Action(ScrollToBottom)
  scrollToBottom({ patchState }: StateContext<ChatStateModel>): void {
    patchState({ isAnimationPlayed: true });
  }

  @Action(SendMessage)
  send({ getState, dispatch }: StateContext<ChatStateModel>, { payload }: SendMessage): Observable<void | HttpErrorResponse> {
    const { chatId } = getState();
    const { game, value } = payload;
    return this.#api.addMessage(chatId, { value, game }).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 400) {
          dispatch(new CheckCanChat());
          this.#notyService.addErrorNotification(error.error.message);
        }
        return of(error);
      }),
    );
  }

  @Action(GetMessagesList)
  getAll({ getState, patchState }: StateContext<ChatStateModel>): void {
    const { chatId } = getState();
    return this.#api.getMessagesList(chatId, (msgs: IMessage[]) => {
      patchState({ items: msgs.slice(-CHAT_MESSAGES_LIMIT) });
    });
  }

  @Action(CheckCanChat)
  checkCanChat({ patchState }: StateContext<ChatStateModel>): Observable<IChatAccess> {
    return this.#userApi.checkCanChat().pipe(tap((response: IChatAccess) => patchState({ chatAccess: response })));
  }

  @Action(CreateMsgEventHandling)
  createdNew({ getState, patchState }: StateContext<ChatStateModel>, { payload }: CreateMsgEventHandling): void {
    patchState({ items: [...getState().items, payload].slice(-CHAT_MESSAGES_LIMIT) });
  }

  @Action(DeleteMsgEventHandling)
  remove({ setState }: StateContext<ChatStateModel>, { payload }: DeleteMsgEventHandling): void {
    setState(patch({ items: removeItem<IMessage>((x) => x?.id === payload.id) }));
  }

  @Action(SetActiveStickerPack)
  setStickerPack({ patchState, getState }: StateContext<ChatStateModel>, { name }: SetActiveStickerPack): void {
    const { activeStickerPack, stickers } = getState();
    if (name === activeStickerPack.name) {
      return;
    }
    const pack = stickers.find((collection: IStickerPack) => collection.name === name) || activeStickerPack;
    patchState({ activeStickerPack: pack });
  }

  @Action(SetChatID)
  setChatId({ patchState }: StateContext<ChatStateModel>, { chatId }: SetChatID): void {
    patchState({ chatId });
  }
}
